import { useTranslation } from 'react-i18next';
import { getRbdDragHandleInstructions, getRbdResponders } from 'core/utils/dragAndDropScreenReader';
import type { RbdDragStart, RbdDragUpdate, RbdDropResult, RbdResponderProvided } from 'core/utils/dragAndDropScreenReader';

type UseDraggableItemsListParams = {
  disableDrag?: boolean;
  disableEditMode?: boolean;
  dragHandleAriaLabel?: string;
  handleOnDragEnd?: (result: RbdDropResult) => void;
};

export type UseDraggableItemsListReturn = {
  dragHandleLabel: string;
  dragHandleUsageInstructions: string;
  isDragDisabled: boolean;
  onDragEnd: (result: RbdDropResult, provided: RbdResponderProvided) => void;
  onDragStart: (start: RbdDragStart, provided: RbdResponderProvided) => void;
  onDragUpdate: (update: RbdDragUpdate, provided: RbdResponderProvided) => void;
};

function useDraggableItemsList({
  disableDrag = false,
  disableEditMode = true,
  dragHandleAriaLabel,
  handleOnDragEnd,
}: UseDraggableItemsListParams): UseDraggableItemsListReturn {
  const { t } = useTranslation();

  const isDragDisabled = disableEditMode || disableDrag;

  // Escape hatch de contexto (p. ej. "reordenar módulos" vs "reordenar
  // contenidos"); por defecto usa la clave genérica del DS.
  const dragHandleLabel = dragHandleAriaLabel ?? t('common:DragToReorder');

  function onDrop(result: RbdDropResult): void {
    handleOnDragEnd?.(result);
  }

  const { onDragEnd, onDragStart, onDragUpdate } = getRbdResponders<RbdDragStart, RbdDragUpdate, RbdDropResult>(t, { onDragEnd: onDrop });

  return {
    dragHandleLabel,
    dragHandleUsageInstructions: getRbdDragHandleInstructions(t),
    isDragDisabled,
    onDragEnd,
    onDragStart,
    onDragUpdate,
  };
}

export default useDraggableItemsList;
